import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Container, Stack, Title, Text, Paper, TextInput, Button, Group,
  Switch, Loader, Center, Select, Alert,
} from '@mantine/core'
import { IconArrowLeft, IconUsers, IconAlertCircle } from '@tabler/icons-react'
import { storyService } from '../services/storyService'
import { useCreateTeam } from '../hooks/useTeam'

export default function TeamCreate() {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [storyId, setStoryId] = useState(null)
  const [facilitatorPlays, setFacilitatorPlays] = useState(false)

  const { data: stories, isLoading: storiesLoading } = useQuery({
    queryKey: ['stories'],
    queryFn: () => storyService.getStories(),
  })

  const createTeam = useCreateTeam()

  // Eligible = Group-owned (in_user_group) OR public. Public stories aren't
  // tied to a paid Group, but admins (and anyone with the invite link) can
  // still play them, so they're teamable too. Must have at least one thread.
  const eligibleStories = useMemo(
    () => (stories || []).filter((s) => (s.in_user_group || s.is_public) && s.threads_count > 0),
    [stories],
  )

  const storyOptions = eligibleStories.map((s) => ({ value: String(s.id), label: s.name }))

  const canSubmit = name.trim().length > 0 && !!storyId && !createTeam.isPending

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit) return
    const team = await createTeam.mutateAsync({
      name: name.trim(),
      firstStoryId: storyId,
      facilitatorPlays,
    })
    if (team?.id) navigate(`/teams/${team.id}`)
  }

  const inputStyles = {
    input: {
      background: 'rgba(10, 22, 40, 0.8)',
      border: '1px solid rgba(0, 255, 255, 0.15)',
      color: '#e0e8f0',
    },
    label: { color: 'var(--mantine-color-gray-4)' },
  }

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <Group>
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/teams')}
          >
            Back to Teams
          </Button>
        </Group>

        <Title order={2} ff="Orbitron, sans-serif" c="cyan.4">
          Create Team
        </Title>

        <Paper p="xl" className="glass">
          {storiesLoading ? (
            <Center py="lg">
              <Loader color="cyan" />
            </Center>
          ) : eligibleStories.length === 0 ? (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="yellow"
              variant="light"
              title="No eligible stories"
            >
              Teams require Group enrollment. You don't currently have access to any
              Group stories. Enroll in a Group first, then come back to create a team.
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                {createTeam.isError && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    variant="light"
                    title="Could not create team"
                  >
                    {createTeam.error?.response?.data?.error ||
                      createTeam.error?.message ||
                      'Unexpected error'}
                  </Alert>
                )}
                <TextInput
                  label="Team name"
                  placeholder="e.g. Q2 Leadership Cohort"
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  required
                  styles={inputStyles}
                />

                <Select
                  label="First story"
                  placeholder="Pick a story to start with"
                  data={storyOptions}
                  value={storyId}
                  onChange={setStoryId}
                  required
                  styles={inputStyles}
                  description="Only stories you have Group access to are listed."
                />

                <Switch
                  label="I will play through the stories alongside the team"
                  checked={facilitatorPlays}
                  onChange={(e) => setFacilitatorPlays(e.currentTarget.checked)}
                  color="cyan"
                  styles={{ label: { color: 'var(--mantine-color-gray-4)' } }}
                />

                <Group justify="flex-end" mt="md">
                  <Button
                    type="submit"
                    leftSection={<IconUsers size={16} />}
                    loading={createTeam.isPending}
                    disabled={!canSubmit}
                  >
                    Create Team
                  </Button>
                </Group>
              </Stack>
            </form>
          )}
        </Paper>

        <Text size="xs" c="dimmed">
          Members must be enrolled in the same Group to join. They'll receive an invite link
          you can share after the team is created.
        </Text>
      </Stack>
    </Container>
  )
}
